/**
 * DateRangeField — a from/to date-range filter field with the app's
 * `"YYYY-MM-DD"` string contract (`startDate` / `endDate`, `onChange(start,
 * end)`), the shape the billing / docs / logs / order-request filter sheets
 * already use.
 *
 * A controlled `Popover` + range `Calendar` (react-day-picker) — the pattern
 * the app's filter bars use. The popover's open state is owned here and only an
 * outside-click closes it, so a from→to range can be picked across two clicks.
 *
 * **Why not the DS `DateRangePicker` atom?** The atom auto-closes the instant
 * `from && to` are both set, and its react-day-picker returns a *same-day*
 * `{from, to}` on the very first click (range `min` defaults to `0`). The net
 * effect is a picker that closes after one click — you can never reach the end
 * date — with no prop to disable it. This adapter replaces it so the range
 * actually works, while keeping the same `"YYYY-MM-DD"` string contract so
 * consumers need no changes.
 */

import { useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface DateRangeFieldProps {
  /** Range start as `"YYYY-MM-DD"`, or undefined/"" when unset. */
  startDate?: string;
  /** Range end as `"YYYY-MM-DD"`, or undefined/"" when unset. */
  endDate?: string;
  /** Emits `(startDate, endDate)` as `"YYYY-MM-DD"` or `undefined` when cleared. */
  onChange: (startDate: string | undefined, endDate: string | undefined) => void;
  /** Latest selectable date (later dates disabled). */
  max?: Date;
  /** Earliest selectable date (earlier dates disabled). */
  min?: Date;
  placeholder?: string;
  disabled?: boolean;
  /** Optional field label (sentence case, medium weight) rendered above the trigger. */
  label?: string;
  id?: string;
  className?: string;
  /**
   * When to emit `onChange`:
   *  - `'select'` (default): on every calendar pick — the live-update contract
   *    the filter sheets rely on.
   *  - `'close'`: buffered; emits once when the popover closes. Use when each
   *    emit is expensive (e.g. re-queries a whole dashboard).
   */
  commitOn?: 'select' | 'close';
}

/** Parse `"YYYY-MM-DD"` (or `"…T…"`) into a local-midnight Date. */
function parseYmd(v?: string): Date | undefined {
  if (!v) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? undefined : d;
}

/** Serialize a Date to `"YYYY-MM-DD"` (local calendar day, no TZ shift). */
function toYmd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** "18 Apr 2026" for the trigger / footer label. */
function fmtDate(d: Date): string {
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function labelFor(range: DateRange | undefined, placeholder: string): string {
  if (!range?.from) return placeholder;
  return range.to
    ? `${fmtDate(range.from)} – ${fmtDate(range.to)}`
    : fmtDate(range.from);
}

export function DateRangeField({
  startDate,
  endDate,
  onChange,
  max,
  min,
  placeholder = 'Select a date range',
  disabled = false,
  label,
  id,
  className,
  commitOn = 'select',
}: DateRangeFieldProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>(undefined);

  const propFrom = parseYmd(startDate);
  const propRange: DateRange | undefined = propFrom
    ? { from: propFrom, to: parseYmd(endDate) }
    : undefined;

  // While open, the trigger reflects the in-progress draft; when closed it
  // reflects the committed props.
  const shown = open ? draft : propRange;

  const emit = (r: DateRange | undefined) =>
    onChange(
      r?.from ? toYmd(r.from) : undefined,
      r?.to ? toYmd(r.to) : undefined,
    );

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraft(propRange); // seed from committed value on open
    } else if (commitOn === 'close') {
      emit(draft); // commit the picked range once, on close
    }
    setOpen(next);
  };

  const handleSelect = (r: DateRange | undefined) => {
    setDraft(r);
    if (commitOn === 'select') emit(r);
  };

  const trigger = (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        render={
          <button
            id={id}
            type="button"
            disabled={disabled}
            className={cn(
              'flex h-8 w-full items-center gap-2 rounded-lg border bg-white px-3 text-size-sm outline-none transition-colors',
              open ? 'border-brand-primary' : 'border-border-strong hover:border-text-muted',
              disabled && 'cursor-not-allowed bg-grey-100',
            )}
          />
        }
      >
        <CalendarIcon className="w-4 h-4 shrink-0 text-text-muted" aria-hidden />
        <span className={cn('truncate', shown?.from ? 'text-text-primary' : 'text-text-muted')}>
          {labelFor(shown, placeholder)}
        </span>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2">
        <Calendar
          mode="range"
          selected={draft}
          onSelect={handleSelect}
          numberOfMonths={1}
          autoFocus
          startMonth={min}
          endMonth={max}
          disabled={[
            ...(min ? [{ before: min }] : []),
            ...(max ? [{ after: max }] : []),
          ]}
        />
        <div className="mt-2 flex items-center justify-between border-t border-border-default pt-2 text-size-sm">
          <button
            type="button"
            onClick={() => handleSelect(undefined)}
            className="font-medium text-brand-primary hover:underline"
          >
            Clear
          </button>
          <span className="text-text-muted">{labelFor(draft, '—')}</span>
        </div>
      </PopoverContent>
    </Popover>
  );

  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      {label && (
        <label
          htmlFor={id}
          className="block text-xs font-medium text-text-muted"
        >
          {label}
        </label>
      )}
      {trigger}
    </div>
  );
}
